import { applyPct, mulQ4 } from "./arith";
import { allocateProportional } from "./commission";
import { DomainError } from "./errors";
import { MAX_DOCUMENT_CENTS, MAX_ITEM_DISCOUNT_PCT4, MAX_QUOTE_ITEMS } from "./quotes";
import { MAX_STOCK_QTY_Q4 } from "./stock";
import type { BaseState, TransitionAction } from "./status-flow";

// ===== Pedidos de compra e recebimento parcial (RF-SUP-03; 05 §3, §4, §5) =====

/** Itens por pedido (como a proposta e o pedido de venda: a tela mostra todos). */
export const MAX_PURCHASE_ORDER_ITEMS = MAX_QUOTE_ITEMS;
/** Recebimentos ativos (não estornados) por pedido: limita o trabalho do estorno em massa da transição. */
export const MAX_ACTIVE_RECEIPTS = 50;
/** IPI do item vai de 0% a 100% (pct4). */
export const MAX_IPI_PCT4 = 1_000_000;

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};
const isInt = (v: number) => Number.isSafeInteger(v);
const money = (v: number, what: string) => {
  if (!isInt(v) || v < 0) fail("invalid_money", `${what} inválido`);
  return v;
};

export type PurchaseLineInput = { qtyQ4: number; unitPriceCents: number; ipiPct4: number };
export type PurchaseCalcInput = {
  items: readonly PurchaseLineInput[];
  /** Desconto geral em % sobre os produtos; null = em valor (`discountCents`). */
  discountPct4: number | null;
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  /** ICMS-ST estimado do pedido (valor). */
  icmsStCents: number;
};
export type PurchaseLine = {
  /** Preço × quantidade. */
  productsCents: number;
  ipiCents: number;
  /** Produtos + IPI (coluna "Total" do item). */
  totalCents: number;
  /** Custo do item: total + rateio de ST, frete e outras − rateio do desconto (entra no custo médio). */
  landedCents: number;
};
export type PurchaseTotals = {
  lines: PurchaseLine[];
  productsTotalCents: number;
  ipiTotalCents: number;
  icmsStCents: number;
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  totalCents: number;
  itemsCount: number;
  quantitySumQ4: number;
};

/**
 * Totais do pedido de compra (PRD RF-SUP-03; 05 §3): produtos, IPI por item, ICMS-ST, desconto (valor ou % sobre os
 * produtos, nunca acima deles), frete e outras despesas. Rateio para o custo de cada item (`allocateProportional`):
 * ST, frete e outras pelo total do item (produtos + IPI) e o desconto pelos produtos; diferença de centavos no item de
 * maior valor. Σ custos = total.
 */
export function purchaseOrderTotals(i: PurchaseCalcInput): PurchaseTotals {
  if (i.items.length > MAX_PURCHASE_ORDER_ITEMS)
    fail("too_many_items", `No máximo ${MAX_PURCHASE_ORDER_ITEMS} itens`);
  const freight = money(i.freightCents, "Frete");
  const other = money(i.otherExpensesCents, "Outras despesas");
  const st = money(i.icmsStCents, "ICMS-ST");
  let products = 0n;
  let ipi = 0n;
  let qty = 0n;
  const raw = i.items.map((it) => {
    if (!isInt(it.qtyQ4) || it.qtyQ4 <= 0 || it.qtyQ4 > MAX_STOCK_QTY_Q4)
      fail("invalid_qty", "Quantidade do item inválida");
    money(it.unitPriceCents, "Preço do item");
    if (!isInt(it.ipiPct4) || it.ipiPct4 < 0 || it.ipiPct4 > MAX_IPI_PCT4)
      fail("invalid_ipi", "O IPI do item vai de 0% a 100%");
    let p: number;
    try {
      p = mulQ4(it.unitPriceCents, it.qtyQ4);
    } catch {
      return fail("total_limit", "Total do pedido acima do limite");
    }
    if (p > MAX_DOCUMENT_CENTS) fail("total_limit", "Total do pedido acima do limite");
    const v = applyPct(p, it.ipiPct4);
    products += BigInt(p);
    ipi += BigInt(v);
    qty += BigInt(it.qtyQ4);
    return { productsCents: p, ipiCents: v, totalCents: p + v };
  });
  if (products + ipi + BigInt(st) + BigInt(freight) + BigInt(other) > BigInt(MAX_DOCUMENT_CENTS))
    fail("total_limit", "Total do pedido acima do limite");
  let discount: number;
  if (i.discountPct4 !== null) {
    if (!isInt(i.discountPct4) || i.discountPct4 < 0 || i.discountPct4 > MAX_ITEM_DISCOUNT_PCT4)
      fail("invalid_discount", "O desconto geral vai de 0% a 100%");
    discount = applyPct(Number(products), i.discountPct4);
  } else {
    if (!isInt(i.discountCents) || i.discountCents < 0) fail("invalid_discount", "Desconto geral inválido");
    if (BigInt(i.discountCents) > products)
      fail("invalid_discount", "O desconto geral não pode passar do total dos produtos");
    discount = i.discountCents;
  }
  const extras = raw.length
    ? allocateProportional(
        st + freight + other,
        raw.map((l) => l.totalCents),
      )
    : [];
  const discounts = raw.length
    ? allocateCapped(
        discount,
        raw.map((l) => l.productsCents),
      )
    : [];
  const lines = raw.map((l, k) => ({ ...l, landedCents: l.totalCents + extras[k]! - discounts[k]! }));
  return {
    lines,
    productsTotalCents: Number(products),
    ipiTotalCents: Number(ipi),
    icmsStCents: st,
    discountCents: discount,
    freightCents: freight,
    otherExpensesCents: other,
    totalCents: Number(products + ipi) + st + freight + other - discount,
    itemsCount: raw.length,
    quantitySumQ4: Number(qty),
  };
}

/**
 * Rateio proporcional com teto no peso de cada item (desconto ≤ produtos do item): o resto dos centavos vai para o
 * item de maior valor como em `allocateProportional`, e o que passaria do teto dele segue para os próximos de maior
 * valor. Sem isso o custo do item podia ficar negativo e os recebimentos somarem mais que o total (revisão T2.6).
 * Exige `amount ≤ Σ pesos`.
 */
function allocateCapped(amount: number, weights: readonly number[]): number[] {
  const parts = allocateProportional(amount, weights);
  let excess = 0;
  parts.forEach((p, k) => {
    if (p > weights[k]!) {
      excess += p - weights[k]!;
      parts[k] = weights[k]!;
    }
  });
  if (excess === 0) return parts;
  const order = weights.map((_, k) => k).sort((a, b) => weights[b]! - weights[a]! || a - b);
  for (const k of order) {
    if (excess === 0) break;
    const room = Math.min(excess, weights[k]! - parts[k]!);
    parts[k]! += room;
    excess -= room;
  }
  return parts;
}

// ---------- recebimento ----------

/** Valor acumulado do item com `receivedQ4` recebido: proporcional ao custo do item e exato quando completa. */
export function receivedValueCents(landedCents: number, qtyQ4: number, receivedQ4: number): number {
  if (receivedQ4 <= 0) return 0;
  if (receivedQ4 >= qtyQ4) return landedCents;
  const num = BigInt(landedCents) * BigInt(receivedQ4) * 2n + BigInt(qtyQ4);
  return Number(num / (2n * BigInt(qtyQ4)));
}

/**
 * Valor da linha do recebimento: valor acumulado depois do recebimento − Σ das linhas ativas do item. A soma das
 * linhas ativas fica igual ao valor acumulado (exata enquanto os estornos forem do mais novo para o mais antigo);
 * nunca negativa.
 */
export function receiptLineValue(
  item: { landedCents: number; qtyQ4: number },
  receivedBeforeQ4: number,
  receiveQ4: number,
  activeValueCents: number,
): number {
  return Math.max(
    0,
    receivedValueCents(item.landedCents, item.qtyQ4, receivedBeforeQ4 + receiveQ4) - activeValueCents,
  );
}

type Balance = { qtyQ4: number; receivedQ4: number; canceledQ4: number };

/** Saldo a receber do item (pedido − recebido − cancelado). */
export const remainingQ4 = (it: Balance) => Math.max(0, it.qtyQ4 - it.receivedQ4 - it.canceledQ4);

export type ReceiptProgress = "none" | "partial" | "complete";

/** Andamento do pedido: nada recebido, parcial, ou sem saldo (recebido e/ou cancelado). */
export function receiptProgress(items: readonly Balance[]): ReceiptProgress {
  if (items.length && items.every((it) => remainingQ4(it) === 0)) return "complete";
  return items.some((it) => it.receivedQ4 > 0) ? "partial" : "none";
}

/**
 * Linhas do recebimento conferidas (decisão 4): item do pedido, sem repetição, quantidade inteira ≥ 0 e até o saldo
 * (sem recebimento acima do pedido). Linhas com zero ficam de fora; nenhuma linha positiva = `validation`.
 */
export function receiptPlan(
  items: readonly (Balance & { id: string })[],
  request: readonly { itemId: string; qtyQ4: number }[],
): { itemId: string; qtyQ4: number }[] {
  const byId = new Map(items.map((it) => [it.id, it]));
  const seen = new Set<string>();
  const out: { itemId: string; qtyQ4: number }[] = [];
  for (const r of request) {
    if (seen.has(r.itemId)) fail("validation", "Item repetido no recebimento.");
    seen.add(r.itemId);
    const it = byId.get(r.itemId) ?? fail("not_found", "Item do pedido não encontrado");
    if (!isInt(r.qtyQ4) || r.qtyQ4 < 0) fail("validation", "Quantidade recebida inválida.");
    if (r.qtyQ4 === 0) continue;
    if (r.qtyQ4 > remainingQ4(it))
      fail(
        "receipt_qty_exceeded",
        "A quantidade recebida passa do saldo do item. Receba até o saldo; o excedente entra por movimento de estoque.",
      );
    out.push({ itemId: r.itemId, qtyQ4: r.qtyQ4 });
  }
  if (out.length === 0) fail("validation", "Informe a quantidade recebida de ao menos um item.");
  return out;
}

/** Por que o pedido não pode ser editado (null = pode). */
export function purchaseOrderLockProblem(base: BaseState, activeReceipts: number): string | null {
  if (base === "canceled") return "Pedido cancelado não pode ser alterado.";
  if (base === "done") return "Pedido concluído não pode ser alterado.";
  if (activeReceipts > 0)
    return "O pedido tem recebimento lançado. Estorne os recebimentos antes de alterar.";
  return null;
}

export type PurchaseTransitionPlan = {
  /** Receber o saldo inteiro, lançando estoque e/ou contas. */
  receive: { postStock: boolean; postPayables: boolean } | null;
  /** Estornar todos os recebimentos ativos. */
  reverseAll: boolean;
  /** Reabrir o saldo cancelado (saída de uma situação concluída ou cancelada para aberta/andamento). */
  reopen: boolean;
};

/**
 * Ações da transição da compra em recebimentos (decisão 1): lançar = receber o saldo; estornar, ou destino cancelado,
 * = estornar todos os recebimentos; cancelar nunca recebe.
 */
export function purchaseTransitionPlan(
  actions: readonly TransitionAction[],
  targetBase: BaseState,
  fromBase: BaseState,
): PurchaseTransitionPlan {
  const has = (a: TransitionAction) => actions.includes(a);
  const post = has("post_stock") || has("post_financial");
  return {
    receive:
      targetBase !== "canceled" && post
        ? { postStock: has("post_stock"), postPayables: has("post_financial") }
        : null,
    reverseAll: targetBase === "canceled" || has("reverse_stock") || has("reverse_financial"),
    // Saindo de concluído ou de cancelado para aberto/andamento: o saldo cancelado volta a ser recebível.
    reopen:
      (fromBase === "done" || fromBase === "canceled") &&
      (targetBase === "open" || targetBase === "in_progress"),
  };
}

// ---------- NF-e de entrada → pedido (T2.5) ----------

type OrderBalance = { id: string; productId: string; remainingQ4: number };
type InvoiceLine = { productId: string; qtyQ4: number };

function invoiceByProduct(invoice: readonly InvoiceLine[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const l of invoice) if (l.qtyQ4 > 0) m.set(l.productId, (m.get(l.productId) ?? 0) + l.qtyQ4);
  return m;
}

/**
 * Quantidades da nota (por produto, em unidades do produto) alocadas nos itens do pedido com o mesmo produto, na ordem
 * dos itens, até o saldo de cada um. O que passa do saldo, ou produto fora do pedido, não conta no pedido.
 */
export function allocateInvoiceToOrder(
  order: readonly OrderBalance[],
  invoice: readonly InvoiceLine[],
): { itemId: string; qtyQ4: number }[] {
  const left = invoiceByProduct(invoice);
  const out: { itemId: string; qtyQ4: number }[] = [];
  for (const it of order) {
    const avail = left.get(it.productId) ?? 0;
    const q = Math.min(avail, it.remainingQ4);
    if (q <= 0) continue;
    out.push({ itemId: it.id, qtyQ4: q });
    left.set(it.productId, avail - q);
  }
  return out;
}

/** Conferência da nota contra o pedido: produtos em comum, quantidade casada e se tudo da nota cabe no saldo. */
export function invoiceMatchScore(order: readonly OrderBalance[], invoice: readonly InvoiceLine[]) {
  const wanted = invoiceByProduct(invoice);
  const lines = allocateInvoiceToOrder(order, invoice);
  const productOf = new Map(order.map((it) => [it.id, it.productId]));
  const matched = new Set(lines.map((l) => productOf.get(l.itemId)!));
  const matchedQtyQ4 = lines.reduce((s, l) => s + l.qtyQ4, 0);
  const wantedQtyQ4 = [...wanted.values()].reduce((s, q) => s + q, 0);
  return {
    matchedProducts: matched.size,
    invoiceProducts: wanted.size,
    matchedQtyQ4,
    fits: wanted.size > 0 && matchedQtyQ4 === wantedQtyQ4,
  };
}
